import * as React from 'react'
import { API_BASE_URL } from '@/helpers/apiClient'
import { signService } from '@/services/SignService'
import { parseJsonText } from '@lib/helpers/json'
import {
  questionSseEventSchema,
  type QuestionSseEvent,
} from '@lib/dto/question.dto'
import type { DocumentSourceRoot } from '@/types/SignService.types'

const PRESENCE_MS = 15_000

function parseSseEvent(raw: string): QuestionSseEvent | null {
  try {
    const parsed = questionSseEventSchema.safeParse(parseJsonText(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

/**
 * `EventSource` types `MessageEvent.data` as `any`. Only a string is an SSE
 * payload; anything else is dropped before it can enter the parser.
 */
function ssePayload(data: string | Blob | ArrayBuffer | ArrayBufferView | null | undefined): string | null {
  return typeof data === 'string' ? data : null
}

export function useQuestionRealtime({
  token,
  source,
  enabled,
  onEvent,
}: {
  token: string | undefined
  source: DocumentSourceRoot
  enabled: boolean
  onEvent: (event: QuestionSseEvent) => void
}): void {
  // Always the latest callback, without reconnecting the stream when it changes.
  const handleEvent = React.useEffectEvent(onEvent)

  React.useEffect(() => {
    if (!enabled || !token) {
      return
    }
    const tick = (): void => {
      void signService.touchPresence(token, source)
    }
    tick()
    const handle = window.setInterval(tick, PRESENCE_MS)
    return () => window.clearInterval(handle)
  }, [enabled, token, source])

  React.useEffect(() => {
    if (!enabled || !token) {
      return
    }
    const url = `${API_BASE_URL}/v1/${source}/${encodeURIComponent(token)}/events`
    const stream = new EventSource(url)
    stream.onmessage = (message) => {
      const raw = ssePayload(message.data)
      if (raw === null) {
        return
      }
      const parsed = parseSseEvent(raw)
      if (parsed) {
        handleEvent(parsed)
      }
    }
    return () => stream.close()
  }, [enabled, token, source])
}
